/** Order detail — 2-column layout с status changer + timeline. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Loader2, MapPin, Phone, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { StickySaveBar } from "@/components/common/StickySaveBar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { OrderTimeline } from "@/components/orders/OrderTimeline";
import { InvoicesSection, RefundsSection } from "@/components/orders/OrderActions";
import { api } from "@/lib/api";
import {
  PAYMENT_STATUS_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  type OrderStatus,
} from "@/lib/order-status";
import { formatDate, formatPrice } from "@/lib/utils";

export function OrderShowPage(): JSX.Element {
  const { t } = useTranslation();
  const { id: publicId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [internalNotes, setInternalNotes] = useState("");

  const { data: order, isLoading } = useQuery({
    queryKey: ["admin-order", publicId],
    queryFn: async () => {
      const { data } = await api.get(`/orders/${publicId}`);
      setInternalNotes(data.internal_notes ?? "");
      return data;
    },
  });

  const { data: transitions } = useQuery({
    queryKey: ["admin-order-transitions", publicId],
    queryFn: async () => {
      const { data } = await api.get(`/orders/${publicId}/allowed-transitions`);
      return data.transitions as string[];
    },
    enabled: Boolean(publicId),
  });

  const statusMutation = useMutation({
    mutationFn: async (toStatus: string) => {
      await api.post(`/orders/${publicId}/status`, { to_status: toStatus });
    },
    onSuccess: () => {
      toast.success(t("orders.statusUpdated"));
      queryClient.invalidateQueries({ queryKey: ["admin-order", publicId] });
      queryClient.invalidateQueries({ queryKey: ["admin-order-transitions", publicId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.errorOccurred")),
  });

  const notesMutation = useMutation({
    mutationFn: async (notes: string) => {
      await api.patch(`/orders/${publicId}`, { internal_notes: notes });
    },
    onSuccess: () => {
      toast.success(t("orders.notesSaved"));
      queryClient.invalidateQueries({ queryKey: ["admin-order", publicId] });
    },
  });

  const notesDirty = (order?.internal_notes ?? "") !== internalNotes;

  if (isLoading || !order) return <Skeleton className="h-96" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main column */}
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading">{order.order_number}</h1>
            <p className="text-sm text-muted-foreground">
              {formatDate(order.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_VARIANTS[order.status as OrderStatus]} className="text-base px-3 py-1">
              {STATUS_LABELS[order.status as OrderStatus]}
            </Badge>
          </div>
        </div>

        {/* Status changer */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Изменить статус</CardTitle>
          </CardHeader>
          <CardContent>
            {transitions && transitions.length > 0 ? (
              <Select onValueChange={(v) => statusMutation.mutate(v)}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder={t("orders.transitionTo")} />
                </SelectTrigger>
                <SelectContent>
                  {transitions.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s as keyof typeof STATUS_LABELS] ?? s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">
                Финальный статус — переходы недоступны
              </p>
            )}
            {statusMutation.isPending && (
              <Loader2 className="w-4 h-4 animate-spin mt-2" />
            )}
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Позиции ({order.items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {order.items.map((it: any) => (
                <div
                  key={it.id}
                  className="py-3 flex items-center gap-3 first:pt-0 last:pb-0"
                >
                  {it.image_url ? (
                    <img
                      src={it.image_url}
                      alt=""
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-muted rounded" />
                  )}
                  <div className="flex-1">
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-xs text-muted-foreground">
                      SKU: {it.product_sku} × {it.quantity}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatPrice(it.line_total)}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatPrice(it.unit_price)} × {it.quantity}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardContent className="pt-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Товары:</span>
              <span>{formatPrice(order.items_total)}</span>
            </div>
            {order.delivery_cost > 0 && (
              <div className="flex justify-between text-sm">
                <span>Доставка:</span>
                <span>{formatPrice(order.delivery_cost)}</span>
              </div>
            )}
            {order.promo_discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Промо:</span>
                <span>−{formatPrice(order.promo_discount)}</span>
              </div>
            )}
            {order.bonus_used > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Бонусы:</span>
                <span>−{formatPrice(order.bonus_used)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Итого:</span>
              <span>{formatPrice(order.grand_total)}</span>
            </div>
          </CardContent>
        </Card>

        {/* C2: Refunds + Invoices */}
        <RefundsSection orderId={order.id} orderItems={order.items} />
        <InvoicesSection orderId={order.id} />

        {/* Timeline (Phase C1) — все события, не только статусы */}
        <OrderTimeline orderId={order.public_id} />

        {/* Legacy status_log — оставлено для backwards-compat */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">История статусов (legacy)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {order.status_log.map((log: any, i: number) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                    {i < order.status_log.length - 1 && (
                      <div className="flex-1 w-px bg-border my-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="text-sm font-medium">
                      {log.from_status
                        ? `${STATUS_LABELS[log.from_status as keyof typeof STATUS_LABELS] ?? log.from_status} → `
                        : ""}
                      {STATUS_LABELS[log.to_status as keyof typeof STATUS_LABELS] ?? log.to_status}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(log.created_at)}
                      {log.reason && ` · ${log.reason}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Internal notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Внутренние заметки</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder={t("orders.internalNotesPlaceholder")}
              rows={3}
            />
            <p className="mt-1.5 text-[10px] text-muted-foreground">
              Видны только staff. Сохранение через панель внизу страницы.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Side column */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4" /> Клиент
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="font-medium">{order.contact_name}</div>
            <a
              href={`tel:${order.contact_phone}`}
              className="flex items-center gap-2 text-primary hover:underline"
            >
              <Phone className="w-3 h-3" />
              {order.contact_phone}
            </a>
            {order.contact_email && (
              <div className="text-xs text-muted-foreground">
                {order.contact_email}
              </div>
            )}
            {order.client_id && (
              <Link
                to={`/clients/${order.client_id}`}
                className="text-xs text-primary hover:underline"
              >
                Профиль клиента →
              </Link>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Доставка
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {order.delivery_address ? (
              <>
                <div>{order.delivery_address.city}</div>
                {order.delivery_address.address_line && (
                  <div>{order.delivery_address.address_line}</div>
                )}
                {order.delivery_address.landmark && (
                  <div className="text-muted-foreground text-xs">
                    {order.delivery_address.landmark}
                  </div>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">Самовывоз</span>
            )}
            <div className="pt-2 text-xs text-muted-foreground">
              Стоимость: {formatPrice(order.delivery_cost)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Оплата
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <Badge
              variant={order.payment_status === "paid" ? "success" : "warning"}
            >
              {PAYMENT_STATUS_LABELS[order.payment_status] ?? order.payment_status}
            </Badge>
            {order.payment_provider && (
              <div className="text-xs text-muted-foreground mt-1">
                Способ: {order.payment_provider}
              </div>
            )}
            {order.paid_at && (
              <div className="text-xs text-muted-foreground">
                Оплачено: {formatDate(order.paid_at)}
              </div>
            )}
          </CardContent>
        </Card>

        {order.notes && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Комментарий клиента</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">{order.notes}</CardContent>
          </Card>
        )}
      </div>

      <StickySaveBar
        visible={notesDirty}
        saving={notesMutation.isPending}
        onSave={() => notesMutation.mutate(internalNotes)}
        onDiscard={() => setInternalNotes(order.internal_notes ?? "")}
        message="Заметки изменены"
      />
    </div>
  );
}
